<script setup>
// AS CREDENCIAIS DO RAMAL — mostradas UMA vez (ao criar e ao gerar senha nova).
// Depois de fechar, ninguém vê a senha de novo, nem o administrador: só
// gerando outra. Por isso o aviso em âmbar e o "Copiar tudo".
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { credenciaisEmTexto } from 'dashboard/helper/telefonia';

const props = defineProps({
  credenciais: { type: Object, required: true },
  ramal: { type: Object, required: true },
});
const emit = defineEmits(['close']);

const { t } = useI18n();
const T = 'TELEFONIA.CRED';

const mostrarSenha = ref(false);
const copiado = ref('');
let tempo = null;

const campos = computed(() => [
  {
    chave: 'servidor',
    rotulo: t(`${T}.SERVIDOR`),
    valor: props.credenciais.servidor,
  },
  {
    chave: 'porta',
    rotulo: t(`${T}.PORTA`),
    valor: String(props.credenciais.porta),
  },
  {
    chave: 'usuario',
    rotulo: t(`${T}.USUARIO`),
    valor: props.credenciais.usuario,
  },
  {
    chave: 'senha',
    rotulo: t(`${T}.SENHA`),
    valor: props.credenciais.senha,
    segredo: true,
  },
  {
    chave: 'transporte',
    rotulo: t(`${T}.TRANSPORTE`),
    valor: props.credenciais.transporte,
  },
]);

const sub = computed(() =>
  t(`${T}.SUB`, {
    numero: props.ramal.numero,
    pessoa: props.ramal.usuario ? ` · ${props.ramal.usuario.nome}` : '',
  })
);

async function copiar(chave, texto) {
  await copyTextToClipboard(texto);
  copiado.value = chave;
  clearTimeout(tempo);
  tempo = setTimeout(() => {
    copiado.value = '';
  }, 1600);
}

const copiarTudo = () =>
  copiar(
    'tudo',
    credenciaisEmTexto(props.credenciais, {
      servidor: t(`${T}.SERVIDOR`),
      porta: t(`${T}.PORTA`),
      usuario: t(`${T}.USUARIO`),
      senha: t(`${T}.SENHA`),
      transporte: t(`${T}.TRANSPORTE`),
    })
  );
</script>

<template>
  <div class="advup-modal-fundo">
    <div
      v-modal-acessivel="() => emit('close')"
      class="advup-modal advup-modal--estreito tel-cred"
    >
      <div class="advup-modal__topo advup-modal__topo--teal">
        <span class="advup-modal__selo" aria-hidden="true">
          <i class="i-ph-sim-card-fill" />
        </span>
        <div class="min-w-0 flex-1">
          <h3 class="advup-modal__titulo">{{ t(`${T}.TIT`) }}</h3>
          <p class="advup-modal__sub">{{ sub }}</p>
        </div>
      </div>

      <div class="advup-modal__corpo tel-cred__corpo">
        <p class="tel-cred__aviso" role="note">
          <i class="i-ph-key-fill" aria-hidden="true" />
          <span>{{ t(`${T}.AVISO`) }}</span>
        </p>

        <dl class="tel-cred__lista">
          <div
            v-for="(c, i) in campos"
            :key="c.chave"
            class="tel-cred__linha"
            :class="{ 'tel-cred__linha--segredo': c.segredo }"
            :style="{ '--i': i }"
          >
            <dt class="tel-cred__rot">{{ c.rotulo }}</dt>
            <dd class="tel-cred__valor">
              <code class="tel-cred__dado">
                {{ c.segredo && !mostrarSenha ? '•'.repeat(16) : c.valor }}
              </code>
              <button
                v-if="c.segredo"
                v-tooltip="
                  mostrarSenha ? t(`${T}.ESCONDER`) : t(`${T}.MOSTRAR`)
                "
                type="button"
                class="advup-acao advup-acao--p"
                :aria-label="
                  mostrarSenha ? t(`${T}.ESCONDER`) : t(`${T}.MOSTRAR`)
                "
                @click="mostrarSenha = !mostrarSenha"
              >
                <i
                  :class="
                    mostrarSenha ? 'i-hugeicons-view-off' : 'i-hugeicons-view'
                  "
                  aria-hidden="true"
                />
              </button>
              <button
                v-tooltip="
                  copiado === c.chave
                    ? t(`${T}.COPIADO`)
                    : t(`${T}.COPIAR`, { campo: c.rotulo })
                "
                type="button"
                class="advup-acao advup-acao--p advup-acao--marca"
                :class="{ 'advup-acao--confirmado': copiado === c.chave }"
                :aria-label="t(`${T}.COPIAR`, { campo: c.rotulo })"
                @click="copiar(c.chave, c.valor)"
              >
                <i
                  :class="
                    copiado === c.chave
                      ? 'i-hugeicons-tick-02'
                      : 'i-hugeicons-copy-01'
                  "
                  aria-hidden="true"
                />
              </button>
            </dd>
          </div>
        </dl>

        <button
          type="button"
          class="advup-btn tel-cred__tudo"
          @click="copiarTudo"
        >
          <i
            :class="
              copiado === 'tudo' ? 'i-ph-check-bold' : 'i-ph-copy-simple-bold'
            "
            aria-hidden="true"
          />
          {{ copiado === 'tudo' ? t(`${T}.COPIADO`) : t(`${T}.COPIAR_TUDO`) }}
        </button>

        <section class="tel-cred__como">
          <p class="advup-rotulo">{{ t(`${T}.COMO_TIT`) }}</p>
          <ol class="tel-cred__passos">
            <li>{{ t(`${T}.PASSO_1`) }}</li>
            <li>{{ t(`${T}.PASSO_2`) }}</li>
            <li>{{ t(`${T}.PASSO_3`) }}</li>
          </ol>
        </section>
      </div>

      <div class="advup-modal__rodape advup-modal__rodape--acoes">
        <button type="button" class="advup-btn-forte" @click="emit('close')">
          <i class="i-ph-check-bold" aria-hidden="true" />
          {{ t(`${T}.FECHAR`) }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tel-cred__corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-4);
}

.tel-cred__aviso {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-2);
  margin: 0;
  padding: var(--esp-3);
  border-radius: var(--r-3);
  background: rgb(var(--amber-3));
  font-size: var(--txt-nota);
  font-weight: var(--peso-dado);
  line-height: var(--linha-solta);
  color: color-mix(in srgb, rgb(var(--amber-11)) 80%, rgb(var(--amber-12)));
}

.tel-cred__aviso > i {
  flex-shrink: 0;
  margin-top: 0.15em;
  font-size: var(--ico-2);
  color: rgb(var(--amber-9));
}

.tel-cred__lista {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  margin: 0;
  padding: var(--esp-2);
  border-radius: var(--r-4);
  background: rgb(var(--slate-2));
}

.tel-cred__linha {
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  align-items: center;
  gap: var(--esp-2);
  padding: var(--esp-1) var(--esp-2);
  border-radius: var(--r-3);
  animation: tel-cred-entra var(--mov-2) var(--mov-curva) backwards;
  animation-delay: calc(var(--i, 0) * var(--cascata-passo));
}

.tel-cred__linha--segredo {
  background: rgb(var(--solid-1));
  box-shadow: 0 0 0 1px rgb(var(--teal-6));
}

.tel-cred__rot {
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  line-height: var(--linha-apertada);
  letter-spacing: var(--rastro-rotulo);
  text-transform: uppercase;
  color: rgb(var(--slate-11));
}

.tel-cred__valor {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  min-width: 0;
  margin: 0;
}

.tel-cred__dado {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: var(--txt-corpo);
  line-height: var(--linha-normal);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgb(var(--slate-12));
}

.tel-cred__tudo {
  align-self: stretch;
  justify-content: center;
}

.tel-cred__como {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
}

.tel-cred__passos {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  margin: 0;
  padding-left: var(--esp-5);
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--slate-11));
}

@keyframes tel-cred-entra {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
}

@media (max-width: 639px) {
  .tel-cred__linha {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }
}
</style>
